import moment from "moment";
import { ProjectPlanResponse } from "./project-plan-builder";
import {
    checkLink,
    endPlaces,
    linkCaption,
    placeOf,
    PlanLinkEndResponse,
    WorkDays as LinkWorkDays,
} from "./plan-dependencies";

/**
 * Конфликты планового графика (п. 7 ТЗ «Графики»). Считаются на лету по ответу графика
 * с уже наложенным черновиком — одинаково для опубликованного плана и для предпросмотра
 * правок. Конфликты не блокируют ни правку, ни применение черновика: их видно, а
 * перегрузку и выход за срок можно пометить допустимыми с комментарием.
 *
 * Видно только то, что попало в загруженный диапазон, и только от сегодняшнего дня:
 * прошлое не правится, конфликтовать там не с чем.
 */

const API_DAY = "YYYY-MM-DD";
const DAY = "DD.MM.YYYY";

/** Виды конфликтов. overload и deadline можно пометить допустимыми */
export type PlanConflictKind = "overload" | "absence" | "dayoff" | "deadline" | "link";

/** Какой срок нарушен */
export type PlanDeadlineKind = "plan" | "contract" | "section";

export const CONFLICT_KIND_LABELS: { [kind in PlanConflictKind]: string } = {
    overload: "Перегрузка",
    absence: "Работа на отсутствии",
    dayoff: "Работа в нерабочий день",
    deadline: "Выход за срок",
    link: "Нарушенная связь",
};

const DEADLINE_LABELS: { [kind in PlanDeadlineKind]: string } = {
    plan: "плановый срок проекта",
    contract: "срок по договору",
    section: "срок раздела",
};

/** Пометка «Допустимо» с сервера */
export interface ConflictMarkResponse {
    keys: string[];
    comment: string;
    createdBy: string;
    created: string;
}

/** Плановое отсутствие сотрудника */
export interface PlanAbsenceResponse {
    employerId: string;
    day: string;
    type: string;
}

/** День, поделённый между проектами (4 + 4 после сверки): count работ — ещё не перегрузка */
export interface PlanSharedDayResponse {
    employerId: string;
    day: string;
    count: number;
}

/** День работы на выходном или празднике */
export interface PlanDayOffWorkResponse {
    employerId: string;
    orderId: string;
    day: string;
    assignmentId: string | null;
}

/**
 * Дни конфликта на полосе одной работы: по ним рисуется подсветка в строке сотрудника,
 * раздела и проекта. У перегрузки таких записей по одной на каждую работу дня.
 */
export interface PlanConflictCells {
    orderId: string;
    employerId: string;
    /** Раздел работы; пусто — «Без раздела» */
    contentGuid: string;
    days: string[];
    /** Работа плана — к её полосе ведёт переход с конфликта; нет — только строка */
    assignmentId?: string | null;
}

export interface PlanConflict {
    /** Первый ключ — для списка и выделения */
    id: string;
    kind: PlanConflictKind;
    /** Ключи для пометки: по дню у перегрузки, один на срок у выхода за срок */
    keys: string[];
    employerId: string;
    employerName: string;
    /** Проекты конфликта; первый — главный, к нему ведёт переход */
    orderIds: string[];
    contentGuid: string;
    from: string;
    to: string;
    /** Сколько рабочих дней затронуто */
    daysCount: number;
    /** Нарушенный срок у выхода за срок */
    deadline: { kind: PlanDeadlineKind; day: string } | null;
    /** Тип дня отсутствия: «Отпуск», «Больничный» */
    absenceType: string;
    /**
     * Всё отсутствие, на которое легла работа, — подряд идущие дни того же типа, а не только
     * дни конфликта: видно, до какого числа сотрудник отсутствует и куда сдвигать работу
     */
    absencePeriod: { from: string; to: string } | null;
    cells: PlanConflictCells[];
    /** Подпись для панели и истории договора */
    title: string;
    /** Можно пометить допустимым */
    markable: boolean;
    /** Пометка «Допустимо» — если помечены все ключи конфликта */
    mark: ConflictMarkResponse | null;
    /** «0494 ЖД Лизюкова 2» по id проектов конфликта — для подсказки на полосе */
    projectTitles: { [orderId: string]: string };
    /** Нарушенная связь: какая и что требуется */
    link: {
        id: string;
        caption: string;
        from: string;
        to: string;
        edge: "start" | "end";
        required: string;
        /** Предшественник — к нему ведёт переход с дней нарушенной связи */
        fromEnd: PlanLinkEndResponse;
    } | null;
}

/** Работа одного дня: сотрудник × проект × раздел × работа плана */
interface DayWork {
    key: string;
    orderId: string;
    contentGuid: string;
}

interface WorkDays {
    key: string;
    orderId: string;
    employerId: string;
    contentGuid: string;
    days: string[];
}

export function computeConflicts(
    response: ProjectPlanResponse | null,
    today: string = moment().format(API_DAY)
): PlanConflict[] {
    if (!response) {
        return [];
    }

    const days = (response.periods || []).flatMap((period) =>
        (period.days || []).filter((day) => !day.isDayOff).map((day) => day.day)
    );
    const index = new Map(days.map((day, position) => [day, position]));

    const names = new Map<string, string>();
    const works = collectWorks(response, days, names, today);

    const marks = new Map<string, ConflictMarkResponse>();
    (response.conflictMarks || []).forEach((mark) => (mark.keys || []).forEach((key) => marks.set(key, mark)));

    const conflicts = [
        ...overloads(works, index, names, response.sharedDays || []),
        ...absences(works, response.absences || [], days, index, names),
        ...dayOffs(response.dayOffWorks || [], names, today),
        ...deadlines(works, response, names),
        ...brokenLinks(works, response, days, names, today),
    ];

    const titles = new Map(
        (response.orders || []).map((order) => [order.id, `${order.fullInventoryNumber} ${order.name}`])
    );

    conflicts.forEach((conflict) => {
        const mark = conflict.markable ? marks.get(conflict.keys[0]) : undefined;
        conflict.mark = mark && conflict.keys.every((key) => marks.has(key)) ? mark : null;
        conflict.orderIds.forEach((orderId) => (conflict.projectTitles[orderId] = titles.get(orderId) || ""));
    });

    return conflicts.sort(compareConflicts);
}

/** Непомеченные конфликты — для счётчика и окна применения */
export function openConflicts(conflicts: PlanConflict[]): PlanConflict[] {
    return conflicts.filter((conflict) => !conflict.mark);
}

/**
 * Работы графика по дням, только от сегодняшнего. Раздел работы — из режима «Разделы»:
 * там строки разложены по разделам, в режиме «Сотрудники» раздела у отрезка нет.
 */
function collectWorks(response: ProjectPlanResponse, days: string[], names: Map<string, string>, today: string) {
    const works = new Map<string, WorkDays>();

    (response.orders || []).forEach((order) => {
        const sectionOf = new Map<string, string>();

        (order.sections || []).forEach((section) =>
            (section.rows || []).forEach((row) =>
                (row.segments || []).forEach((segment) => {
                    if (segment.assignmentId && !segment.ghost) {
                        sectionOf.set(segment.assignmentId, section.id || "");
                    }
                })
            )
        );

        (order.rows || []).forEach((row) => {
            if (row.kind !== "employer") {
                return;
            }

            names.set(row.id, row.label);

            (row.segments || []).forEach((segment) => {
                if (segment.ghost) {
                    return;
                }

                // дни без работы плана (до раздачи работ) — одна работа на сотрудника в проекте
                const key = segment.assignmentId || `${order.id}/${row.id}`;
                let work = works.get(key);

                if (!work) {
                    work = {
                        key,
                        orderId: order.id,
                        employerId: row.id,
                        contentGuid: segment.assignmentId ? sectionOf.get(segment.assignmentId) || "" : "",
                        days: [],
                    };
                    works.set(key, work);
                }

                const from = segment.from > today ? segment.from : today;
                daysBetween(days, from, segment.to).forEach((day) => work!.days.push(day));
            });
        });
    });

    works.forEach((work) => {
        work.days = Array.from(new Set(work.days)).sort();
    });

    return Array.from(works.values()).filter((work) => work.days.length > 0);
}

/** Рабочие дни сетки в отрезке включительно */
function daysBetween(days: string[], from: string, to: string): string[] {
    const result: string[] = [];
    let position = lowerBound(days, from);

    while (position < days.length && days[position] <= to) {
        result.push(days[position]);
        position++;
    }

    return result;
}

/** Работы сотрудников по дням: сотрудник → день → работы */
function byEmployerDay(works: WorkDays[]): Map<string, Map<string, DayWork[]>> {
    const result = new Map<string, Map<string, DayWork[]>>();

    works.forEach((work) => {
        let employer = result.get(work.employerId);

        if (!employer) {
            employer = new Map();
            result.set(work.employerId, employer);
        }

        work.days.forEach((day) => {
            const list = employer!.get(day) || [];
            list.push({ key: work.key, orderId: work.orderId, contentGuid: work.contentGuid });
            employer!.set(day, list);
        });
    });

    return result;
}

/**
 * Дни сотрудника с одинаковым набором проектов и разделов, идущие подряд по рабочим
 * дням, — один конфликт: «12.10–16.10, проекты 0500 и 0512», а не пять строк по дню.
 * Набор — по проектам, а не по работам: смена работы внутри проекта (разделили работу)
 * для человека тот же конфликт. Работы run.works — по одной на проект и раздел
 */
function groupRuns(
    days: string[],
    worksOf: (day: string) => DayWork[],
    index: Map<string, number>
): { days: string[]; works: DayWork[] }[] {
    const runs: { days: string[]; works: DayWork[]; signature: string }[] = [];

    days.sort().forEach((day) => {
        const byPlace = new Map<string, DayWork>();
        worksOf(day).forEach((work) => byPlace.set(`${work.orderId}/${work.contentGuid}`, work));

        const works = Array.from(byPlace.values());
        const signature = Array.from(byPlace.keys()).sort().join("|");
        const last = runs[runs.length - 1];
        const previous = last ? last.days[last.days.length - 1] : null;

        if (
            last &&
            last.signature === signature &&
            previous !== null &&
            (index.get(day) ?? -1) === (index.get(previous) ?? -2) + 1
        ) {
            last.days.push(day);
        } else {
            runs.push({ days: [day], works, signature });
        }
    });

    return runs;
}

function overloads(
    works: WorkDays[],
    index: Map<string, number>,
    names: Map<string, string>,
    shared: PlanSharedDayResponse[]
): PlanConflict[] {
    // день, поделённый между проектами в пределах нормы, перегрузкой не считается,
    // пока черновик не добавил в него ещё работу
    const allowed = new Map(shared.map((item) => [`${item.employerId}/${item.day}`, item.count]));
    const result: PlanConflict[] = [];

    byEmployerDay(works).forEach((daysMap, employerId) => {
        const busy = Array.from(daysMap.keys()).filter(
            (day) => distinct(daysMap.get(day)!).length > (allowed.get(`${employerId}/${day}`) || 1)
        );

        groupRuns(busy, (day) => distinct(daysMap.get(day)!), index).forEach((run) => {
            const orderIds = unique(run.works.map((work) => work.orderId));

            result.push(
                conflict({
                    kind: "overload",
                    keys: run.days.map((day) => `overload:${employerId}:${day}`),
                    employerId,
                    employerName: names.get(employerId) || "",
                    orderIds,
                    contentGuid: "",
                    days: run.days,
                    cells: run.works.map((work) => ({
                        orderId: work.orderId,
                        employerId,
                        contentGuid: work.contentGuid,
                        days: run.days,
                        // ключ без работы плана — «проект/сотрудник»
                        assignmentId: work.key.includes("/") ? null : work.key,
                    })),
                    markable: true,
                })
            );
        });
    });

    return result;
}

function absences(
    works: WorkDays[],
    list: PlanAbsenceResponse[],
    days: string[],
    index: Map<string, number>,
    names: Map<string, string>
): PlanConflict[] {
    const absent = new Map<string, Map<string, string>>();

    list.forEach((absence) => {
        const days = absent.get(absence.employerId) || new Map<string, string>();
        days.set(absence.day, absence.type);
        absent.set(absence.employerId, days);
    });

    const result: PlanConflict[] = [];

    byEmployerDay(works).forEach((daysMap, employerId) => {
        const off = absent.get(employerId);

        if (!off) {
            return;
        }

        const hit = Array.from(daysMap.keys()).filter((day) => off.has(day));

        groupRuns(hit, (day) => distinct(daysMap.get(day)!), index).forEach((run) => {
            const type = off.get(run.days[0]) || "";

            // отсутствие целиком: от дней конфликта в обе стороны, пока идут дни того же типа
            let first = index.get(run.days[0]) ?? 0;
            let last = index.get(run.days[run.days.length - 1]) ?? first;

            while (first > 0 && off.get(days[first - 1]) === type) {
                first--;
            }

            while (last < days.length - 1 && off.get(days[last + 1]) === type) {
                last++;
            }

            result.push(
                conflict({
                    kind: "absence",
                    keys: run.days.map((day) => `absence:${employerId}:${day}`),
                    employerId,
                    employerName: names.get(employerId) || "",
                    orderIds: unique(run.works.map((work) => work.orderId)),
                    contentGuid: "",
                    days: run.days,
                    absenceType: type,
                    absencePeriod: { from: days[first], to: days[last] },
                    cells: run.works.map((work) => ({
                        orderId: work.orderId,
                        employerId,
                        contentGuid: work.contentGuid,
                        days: run.days,
                    })),
                    markable: false,
                })
            );
        });
    });

    return result;
}

/** Работы на выходных: в шкале этих дней нет, подсветить нечего — только список */
function dayOffs(list: PlanDayOffWorkResponse[], names: Map<string, string>, today: string): PlanConflict[] {
    const groups = new Map<string, PlanDayOffWorkResponse[]>();

    list.filter((item) => item.day >= today).forEach((item) => {
        const key = `${item.employerId}/${item.orderId}`;
        groups.set(key, [...(groups.get(key) || []), item]);
    });

    return Array.from(groups.values()).map((items) => {
        const days = unique(items.map((item) => item.day)).sort();
        const { employerId, orderId } = items[0];

        return conflict({
            kind: "dayoff",
            keys: days.map((day) => `dayoff:${employerId}:${day}`),
            employerId,
            employerName: names.get(employerId) || "",
            orderIds: [orderId],
            contentGuid: "",
            days,
            cells: [],
            markable: false,
        });
    });
}

/**
 * Выход работы за срок: плановый срок проекта, срок по договору, заданный срок раздела.
 * Раздел без заданного срока ограничен самими работами — выйти за него нельзя.
 * Вехи к работам не привязаны, поэтому за веху выйти тоже нельзя.
 */
function deadlines(works: WorkDays[], response: ProjectPlanResponse, names: Map<string, string>): PlanConflict[] {
    const orders = new Map((response.orders || []).map((order) => [order.id, order]));
    const result: PlanConflict[] = [];

    works.forEach((work) => {
        const order = orders.get(work.orderId);

        if (!order) {
            return;
        }

        const terms = order.terms || [];
        const project = terms.find((term) => !term.contentGuid);
        const section = work.contentGuid ? terms.find((term) => term.contentGuid === work.contentGuid) : undefined;

        const limits: { kind: PlanDeadlineKind; day: string | null | undefined }[] = [
            { kind: "section", day: section ? section.end : null },
            { kind: "plan", day: project ? project.end : null },
            { kind: "contract", day: order.contractEnd },
        ];

        limits.forEach(({ kind, day }) => {
            if (!day) {
                return;
            }

            const over = work.days.filter((item) => item > day);

            if (!over.length) {
                return;
            }

            result.push(
                conflict({
                    kind: "deadline",
                    keys: [`deadline:${kind}:${work.key}:${day}`],
                    employerId: work.employerId,
                    employerName: names.get(work.employerId) || "",
                    orderIds: [work.orderId],
                    contentGuid: work.contentGuid,
                    days: over,
                    deadline: { kind, day },
                    cells: [
                        {
                            orderId: work.orderId,
                            employerId: work.employerId,
                            contentGuid: work.contentGuid,
                            days: over,
                        },
                    ],
                    markable: true,
                })
            );
        });
    });

    return result;
}

interface ConflictInput {
    kind: PlanConflictKind;
    keys: string[];
    employerId: string;
    employerName: string;
    orderIds: string[];
    contentGuid: string;
    days: string[];
    cells: PlanConflictCells[];
    markable: boolean;
    deadline?: { kind: PlanDeadlineKind; day: string };
    absenceType?: string;
    absencePeriod?: { from: string; to: string };
    link?: PlanConflict["link"];
}

function conflict(input: ConflictInput): PlanConflict {
    const from = input.days[0];
    const to = input.days[input.days.length - 1];

    return {
        id: input.keys[0],
        kind: input.kind,
        keys: input.keys,
        employerId: input.employerId,
        employerName: input.employerName,
        orderIds: input.orderIds,
        contentGuid: input.contentGuid,
        from,
        to,
        daysCount: input.days.length,
        deadline: input.deadline || null,
        absenceType: input.absenceType || "",
        absencePeriod: input.absencePeriod || null,
        cells: input.cells,
        title: [
            input.deadline
                ? `Выход за ${DEADLINE_LABELS[input.deadline.kind]} ${formatDay(input.deadline.day)}`
                : CONFLICT_KIND_LABELS[input.kind],
            input.employerName,
            formatRange(from, to),
        ]
            .filter(Boolean)
            .join(" · "),
        markable: input.markable,
        mark: null,
        projectTitles: {},
        link: input.link || null,
    };
}

/**
 * Нарушенные связи (п. 6–7 ТЗ): последователь начинается или кончается раньше, чем велит
 * тип связи и лаг. Положение концов — по плану с черновиком, а что за выборкой — по датам
 * из самой связи. Подсвечиваются дни последователя, которые стоят слишком рано; если
 * ограничен конец — его последний день. Связь, у которой ограниченный край последователя
 * уже в прошлом, не считается: прошлое не правится.
 */
function brokenLinks(
    works: WorkDays[],
    response: ProjectPlanResponse,
    days: string[],
    names: Map<string, string>,
    today: string
): PlanConflict[] {
    const links = response.links || [];

    if (!links.length) {
        return [];
    }

    const places = endPlaces(response);
    const calendar = new LinkWorkDays(days);
    const result: PlanConflict[] = [];

    links.forEach((link) => {
        if (!link.from || !link.to) {
            return;
        }

        const from = placeOf(link.from, places, "from", today);
        const to = placeOf(link.to, places, "to", today);

        if (!from || !to) {
            return;
        }

        const check = checkLink(link.type, link.lag, from, to, calendar);

        if (!check.violated || check.actual < today) {
            return;
        }

        const successor = link.to;
        const own = works.filter((work) =>
            successor.kind === "work"
                ? work.key === successor.assignmentId
                : work.orderId === successor.orderId && work.contentGuid === (successor.contentGuid || "")
        );

        const cells = own
            .map((work) => {
                // у разорванной работы — только кусок, к которому относится связь
                const part =
                    successor.kind === "work" ? work.days.filter((day) => day >= to.start && day <= to.end) : work.days;

                return {
                    orderId: work.orderId,
                    employerId: work.employerId,
                    contentGuid: work.contentGuid,
                    days: check.edge === "start" ? part.filter((day) => day < check.required) : part.slice(-1),
                };
            })
            .filter((cell) => cell.days.length > 0);

        const conflictDays = Array.from(new Set(cells.flatMap((cell) => cell.days))).sort();
        const employerId = successor.kind === "work" ? successor.employerId || "" : "";
        const caption = linkCaption(link.type, link.lag);

        result.push(
            conflict({
                kind: "link",
                keys: [`link:${link.id}`],
                employerId,
                employerName: names.get(employerId) || "",
                orderIds: Array.from(new Set([successor.orderId, link.from.orderId])),
                contentGuid: successor.contentGuid || "",
                days: conflictDays.length ? conflictDays : [check.actual],
                cells,
                markable: false,
                link: {
                    id: link.id,
                    caption,
                    from: link.from.label,
                    to: successor.label,
                    edge: check.edge,
                    required: check.required,
                    fromEnd: link.from,
                },
            })
        );
    });

    return result;
}

/** «12.10.2026» или «12.10–16.10.2026» */
export function formatRange(from: string, to: string): string {
    if (!from) {
        return "";
    }

    if (from === to) {
        return formatDay(from);
    }

    const start = moment(from, API_DAY);
    const end = moment(to, API_DAY);

    return start.year() === end.year()
        ? `${start.format("DD.MM")}–${end.format(DAY)}`
        : `${start.format(DAY)}–${end.format(DAY)}`;
}

export function formatDay(day: string): string {
    return moment(day, API_DAY).format(DAY);
}

const KIND_ORDER: PlanConflictKind[] = ["overload", "absence", "deadline", "link", "dayoff"];

function compareConflicts(left: PlanConflict, right: PlanConflict): number {
    return (
        KIND_ORDER.indexOf(left.kind) - KIND_ORDER.indexOf(right.kind) ||
        (left.from < right.from ? -1 : left.from > right.from ? 1 : 0) ||
        left.employerName.localeCompare(right.employerName)
    );
}

/** Одна работа, попавшая в день двумя отрезками, — не перегрузка */
function distinct(works: DayWork[]): DayWork[] {
    const seen = new Set<string>();
    return works.filter((work) => (seen.has(work.key) ? false : (seen.add(work.key), true)));
}

function unique<T>(items: T[]): T[] {
    return Array.from(new Set(items));
}

function lowerBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] < value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low;
}

/* ---------- подсветка на сетке ---------- */

/** Отрезок подсветки в координатах колонок */
export interface ConflictRange {
    start: number;
    length: number;
    kind: PlanConflictKind;
    /** В чём проблема — строки подсказки на днях конфликта */
    notes: string[];
    /** С чем конфликт: другая работа дня (перегрузка), предшественник (связь) — двойной клик ведёт туда */
    targets: ConflictTarget[];
}

/** Вторая сторона конфликта — куда перейти с его дней */
export interface ConflictTarget {
    orderId: string;
    employerId: string | null;
    /** Раздел; пусто — «Без раздела» */
    contentGuid: string;
    assignmentId: string | null;
    /** Какой день показать */
    day: string | null;
    /** «0500 Саки К1234» — для подсказки */
    label: string;
}

/**
 * С чем конфликтуют дни строки: у перегрузки — работы того же сотрудника в других проектах
 * и разделах (ячейки конфликта, которые не относятся к строке), у нарушенной связи — предшественник
 */
function targetsOf(conflict: PlanConflict, match: (cells: PlanConflictCells) => boolean): ConflictTarget[] {
    if (conflict.kind === "overload") {
        return conflict.cells
            .filter((cells) => !match(cells))
            .map((cells) => ({
                orderId: cells.orderId,
                employerId: cells.employerId,
                contentGuid: cells.contentGuid,
                assignmentId: cells.assignmentId || null,
                day: cells.days[0] || null,
                label: conflict.projectTitles[cells.orderId] || "",
            }));
    }

    if (conflict.kind === "link" && conflict.link) {
        const end = conflict.link.fromEnd;

        return [
            {
                orderId: end.orderId,
                employerId: end.employerId,
                contentGuid: end.contentGuid || "",
                assignmentId: end.kind === "work" ? end.assignmentId : null,
                day: end.end || end.start,
                label: end.label,
            },
        ];
    }

    return [];
}

const targetKey = (target: ConflictTarget) =>
    target.assignmentId || `${target.orderId}/${target.employerId || ""}/${target.contentGuid}`;

/**
 * Что не так — для подсказки на полосе строки проекта orderId: перегрузка называет
 * другие проекты дня, отсутствие — сам отпуск целиком, выход за срок — нарушенный срок
 */
export function conflictNote(conflict: PlanConflict, orderId: string, withEmployer = false): string {
    const note = noteOf(conflict, orderId);

    // на строке проекта и раздела сходятся разные сотрудники — без фамилии не понять, чей конфликт
    return withEmployer && conflict.employerName ? `${conflict.employerName} — ${note}` : note;
}

function noteOf(conflict: PlanConflict, orderId: string): string {
    const range = formatRange(conflict.from, conflict.to);

    switch (conflict.kind) {
        case "overload": {
            const others = conflict.orderIds.filter((id) => id !== orderId);

            return others.length
                ? `Перегрузка ${range}: ещё ${others.map((id) => conflict.projectTitles[id] || "проект").join(", ")}`
                : `Перегрузка ${range}: две работы в одном дне`;
        }

        case "absence":
            return conflict.absencePeriod
                ? `Работа на отсутствии: ${conflict.absenceType || "отсутствие"} ${formatRange(
                      conflict.absencePeriod.from,
                      conflict.absencePeriod.to
                  )}`
                : `Работа на отсутствии ${range}`;

        case "link":
            return conflict.link
                ? `Нарушена связь ${conflict.link.caption}: ${conflict.link.from} → ${conflict.link.to}; ${
                      conflict.link.edge === "start" ? "начать" : "закончить"
                  } не раньше ${formatDay(conflict.link.required)}`
                : `Нарушена связь ${range}`;

        case "deadline":
            return conflict.deadline
                ? `Выход за ${DEADLINE_LABELS[conflict.deadline.kind]} ${formatDay(conflict.deadline.day)}: ${range}`
                : `Выход за срок ${range}`;

        default:
            return `${CONFLICT_KIND_LABELS[conflict.kind]} ${range}`;
    }
}

/**
 * Подсветка для строки графика: дни конфликтов, подходящих под фильтр, в колонках
 * сетки. Подряд идущие колонки одного вида и с теми же проблемами склеиваются. Помеченные
 * допустимыми не подсвечиваются — решение по ним принято. orderId — проект строки: от него
 * зависит текст подсказки («ещё 0494…»); withEmployer — строка сводная (проект, раздел),
 * в подсказке нужна фамилия.
 */
export function conflictRanges(
    conflicts: PlanConflict[],
    columns: Map<string, number>,
    match: (cells: PlanConflictCells) => boolean,
    orderId = "",
    withEmployer = false
): ConflictRange[] {
    const byKind = new Map<PlanConflictKind, Map<number, Set<string>>>();
    // цели по виду и колонке: ключ цели → цель
    const targetsByKind = new Map<PlanConflictKind, Map<number, Map<string, ConflictTarget>>>();

    conflicts.forEach((item) => {
        if (item.mark) {
            return;
        }

        const note = conflictNote(item, orderId, withEmployer);
        const targets = targetsOf(item, match);

        item.cells.filter(match).forEach((cells) =>
            cells.days.forEach((day) => {
                const column = columns.get(day);

                if (column !== undefined) {
                    const kind = byKind.get(item.kind) || new Map<number, Set<string>>();
                    const notes = kind.get(column) || new Set<string>();
                    notes.add(note);
                    kind.set(column, notes);
                    byKind.set(item.kind, kind);

                    const kindTargets = targetsByKind.get(item.kind) || new Map();
                    const columnTargets = kindTargets.get(column) || new Map<string, ConflictTarget>();
                    targets.forEach((target) => columnTargets.set(targetKey(target), target));
                    kindTargets.set(column, columnTargets);
                    targetsByKind.set(item.kind, kindTargets);
                }
            })
        );
    });

    const result: ConflictRange[] = [];

    byKind.forEach((byColumn, kind) => {
        const sorted = Array.from(byColumn.keys()).sort((left, right) => left - right);
        let previousKey = "";

        sorted.forEach((column, position) => {
            const notes = Array.from(byColumn.get(column)!);
            const key = notes.join("\n");
            const last = result[result.length - 1];
            const targets = Array.from((targetsByKind.get(kind)?.get(column) || new Map()).values());

            if (
                position > 0 &&
                last &&
                last.kind === kind &&
                sorted[position - 1] === column - 1 &&
                key === previousKey
            ) {
                last.length++;
                targets.forEach((target) => {
                    if (!last.targets.some((item) => targetKey(item) === targetKey(target))) {
                        last.targets.push(target);
                    }
                });
            } else {
                result.push({ start: column, length: 1, kind, notes, targets });
            }

            previousKey = key;
        });
    });

    return result;
}

/* ---------- отсутствия в строке сотрудника ---------- */

/** Отсутствие на сетке: подряд идущие рабочие дни одного типа */
export interface AbsenceBand {
    start: number;
    length: number;
    type: string;
    from: string;
    to: string;
}

/**
 * Отсутствия сотрудников полосами в колонках сетки — рисуются под работами в строке
 * сотрудника, чтобы было видно, где отпуск начинается и где кончается
 */
export function absenceBands(list: PlanAbsenceResponse[], columns: Map<string, number>): Map<string, AbsenceBand[]> {
    const byEmployer = new Map<string, { column: number; type: string; day: string }[]>();

    list.forEach((absence) => {
        const column = columns.get(absence.day);

        if (column !== undefined) {
            const items = byEmployer.get(absence.employerId) || [];
            items.push({ column, type: absence.type, day: absence.day });
            byEmployer.set(absence.employerId, items);
        }
    });

    const result = new Map<string, AbsenceBand[]>();

    byEmployer.forEach((items, employerId) => {
        const bands: AbsenceBand[] = [];

        items
            .sort((left, right) => left.column - right.column)
            .forEach((item) => {
                const last = bands[bands.length - 1];

                if (last && last.type === item.type && last.start + last.length === item.column) {
                    last.length++;
                    last.to = item.day;
                } else if (!last || last.start + last.length <= item.column) {
                    bands.push({ start: item.column, length: 1, type: item.type, from: item.day, to: item.day });
                }
            });

        result.set(employerId, bands);
    });

    return result;
}
